import React from 'react';
import { Link } from '@remix-run/react';

interface LinkButtonProps {
  type: 'primary' | 'outline';
  size: 'small' | 'medium' | 'large' | 'xs';
  href: string;
  children: React.ReactNode;
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}
  
const LinkButton: React.FC<LinkButtonProps> = ({ type, size, href, children, target, rel, fullWidth = false, onClick, disabled = false, className = '', icon, iconPosition = 'left' }) => {
  const baseClasses = 'font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 inline-block text-center rounded-3xl whitespace-nowrap';
  
  const typeClasses = {
    primary: 'text-white bg-black border-2 border-black hover:bg-gray-800 focus:ring-black',
    outline: 'text-black bg-transparent border-2 border-black hover:bg-black hover:text-white focus:ring-black',
  };

  const sizeClasses = {
    small: 'px-3 py-1 text-sm',
    medium: 'px-4 py-2 text-sm',
    large: 'px-6 py-3 text-medium',
    xs: 'px-3 py-1 text-xs',
  };

  const linkClasses = `${baseClasses} ${typeClasses[type]} ${sizeClasses[size]} ${fullWidth ? 'w-full' : ''} ${
    icon ? 'inline-flex items-center justify-center gap-0' : ''
  }`;

  if (disabled) {
    return (
      <span 
        className={`${linkClasses} opacity-50 cursor-not-allowed`}
      >
        {icon ? (
          <>
            {iconPosition === 'left' && <span className="mr-2">{icon}</span>}
            <span>{children}</span>
            {iconPosition === 'right' && <span className="ml-2">{icon}</span>}
          </>
        ) : children}
      </span>
    );
  }

  return (
    <Link 
      to={href}
      className={`${linkClasses} ${className}`}
      target={target}
      rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
      onClick={onClick}
    >
      {icon ? (
        <>
          {iconPosition === 'left' && <span className="mr-2">{icon}</span>}
          <span>{children}</span>
          {iconPosition === 'right' && <span className="ml-2">{icon}</span>}
        </>
      ) : children}
    </Link>
  );
};

export default LinkButton;